import {isAnonymousSession, isTokenSuccess, requestToken} from '~/lib/api';
import type {OrganizationInfo, TokenResponse} from '~/lib/api';
import {
  AccessDeniedError,
  NotFoundError,
  RateLimitedError,
} from '~/lib/api/errors';
import {identifyExternalUser} from '~/lib/analytics';
import {TokenStore} from './token-store';
import type {PostHogIdentity} from './token-store';

export interface PreviewToken {
  token: string;
  expiresAt: string;
}

export type AuthState =
  | {status: 'loading'}
  | {
      status: 'authenticated';
      tokenStore: TokenStore;
      // Null for preview tokens, which never go through the tokens endpoint.
      organizationIdentifier: string | null;
      isAnonymous: boolean;
      // False only while the post-identify flag reload is pending. Tokens
      // without PostHog metadata, and preview tokens, are ready immediately.
      flagsReady: boolean;
    }
  | {status: 'verification_required'; organization?: OrganizationInfo}
  | {status: 'not_found'}
  | {status: 'preview_expired'}
  | {status: 'rate_limited'}
  | {status: 'error'};

const REFRESH_BUFFER_MS = 60_000;
const RETRY_DELAY_MS = 30_000;
const RATE_LIMIT_RETRY_MS = 3_000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Conference-day load can trip the tokens endpoint's rate limit on a visitor's
// very first request; one short retry beats a terminal error screen.
async function requestTokenWithRetry(
  identifier: string,
): Promise<TokenResponse> {
  try {
    return await requestToken(identifier);
  } catch (err) {
    if (!(err instanceof RateLimitedError)) {
      throw err;
    }
    await delay(RATE_LIMIT_RETRY_MS);
    return requestToken(identifier);
  }
}

function sameIdentity(
  a: PostHogIdentity | undefined,
  b: PostHogIdentity | undefined,
): boolean {
  return (
    a?.distinct_id === b?.distinct_id &&
    a?.group_key === b?.group_key &&
    a?.is_org_member_viewer === b?.is_org_member_viewer
  );
}

interface AuthSessionOptions {
  identifier: string;
  previewToken?: PreviewToken | null;
}

interface AuthenticateOptions {
  isAnonymous?: boolean;
}

type Settled =
  | 'authenticated'
  | 'terminal'
  | 'transient'
  | 'rate_limited'
  | 'superseded';

/**
 * The viewer's session for one shared entity: bootstraps a token, keeps it
 * refreshed, and reports the resulting access state. `subscribe` and
 * `getSnapshot` are stable arrow properties for `useSyncExternalStore`.
 *
 * `start` and `dispose` may be called more than once in sequence (StrictMode
 * simulates an unmount and remount), so nothing here is one-shot.
 */
export class AuthSession {
  readonly identifier: string;
  readonly isPreview: boolean;

  private readonly previewJwt: string | null;
  private readonly previewStorageKey: string;
  private state: AuthState = {status: 'loading'};
  private readonly listeners = new Set<() => void>();
  private tokenStore: TokenStore | null = null;
  private organization: OrganizationInfo | undefined;
  private activeBootstrap: {run: symbol; done: Promise<boolean>} | null = null;
  private previewTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  // Lets a refresh that outlives clearRefresh() drop its result.
  private refreshEpoch = 0;

  constructor({identifier, previewToken}: AuthSessionOptions) {
    this.identifier = identifier;
    this.previewStorageKey = `preview-token:${identifier}`;
    this.previewJwt =
      previewToken?.token ?? sessionStorage.getItem(this.previewStorageKey);
    this.isPreview = this.previewJwt !== null;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): AuthState => this.state;

  start(): void {
    if (this.previewJwt) {
      this.startPreview(this.previewJwt);
      return;
    }

    this.setState({status: 'loading'});
    void this.bootstrap();
  }

  dispose(): void {
    this.activeBootstrap = null;
    this.clearRefresh();
    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }
  }

  handleVerified = (jwt: string, organizationIdentifier: string): void => {
    this.authenticate(jwt, organizationIdentifier);
  };

  /**
   * Re-run the bootstrap because the access state may have changed, e.g. a
   * GraphQL 403 because the page was made restricted mid-session, or an OTP
   * endpoint refusing because the page went public. A live session is kept
   * while the bootstrap runs: tearing it down would drop the Apollo cache and
   * any in-progress chat for what may turn out to be a token that simply
   * expired before the refresh timer fired.
   *
   * Resolves true once a new token is in place, so a caller whose request was
   * refused can retry it. `deniedJwt` is the token that request carried: a
   * report about a token this session has already replaced, or about a session
   * that has already ended, is answered from the current state without another
   * round trip.
   */
  reauthenticate = (deniedJwt?: string): Promise<boolean> => {
    if (this.isPreview) {
      return Promise.resolve(false);
    }
    if (deniedJwt !== undefined) {
      if (this.state.status !== 'authenticated') {
        return Promise.resolve(false);
      }
      if (deniedJwt !== this.tokenStore?.jwt) {
        return Promise.resolve(true);
      }
    }
    if (this.activeBootstrap) {
      return this.activeBootstrap.done;
    }
    if (this.state.status !== 'authenticated') {
      this.setState({status: 'loading'});
    }
    return this.bootstrap();
  };

  private setState(next: AuthState): void {
    this.state = next;
    this.listeners.forEach((listener) => listener());
  }

  private storeToken(jwt: string): TokenStore {
    if (this.tokenStore) {
      this.tokenStore.update(jwt);
    } else {
      this.tokenStore = new TokenStore(jwt);
    }
    return this.tokenStore;
  }

  private authenticate(
    jwt: string,
    organizationIdentifier: string | null,
    {isAnonymous = false}: AuthenticateOptions = {},
  ): TokenStore {
    const previous = this.state;
    const previousIdentity = this.tokenStore?.posthog;
    const tokenStore = this.storeToken(jwt);
    const identity = tokenStore.posthog;

    if (previous.status === 'authenticated') {
      // The page stays mounted through a re-bootstrap or refresh, so the flag
      // state it was mounted with stands.
      this.setState({
        status: 'authenticated',
        tokenStore,
        organizationIdentifier,
        isAnonymous,
        flagsReady: previous.flagsReady,
      });
      if (identity && !sameIdentity(identity, previousIdentity)) {
        void identifyExternalUser(identity);
      }
    } else {
      const waitForFlags = identity !== undefined && !this.isPreview;

      // Authenticate immediately so the entity query runs alongside the flag
      // reload rather than after it.
      this.setState({
        status: 'authenticated',
        tokenStore,
        organizationIdentifier,
        isAnonymous,
        flagsReady: !waitForFlags,
      });

      if (identity) {
        const ready = identifyExternalUser(identity);
        if (waitForFlags) {
          void ready.then(() => {
            if (
              this.state.status === 'authenticated' &&
              this.state.tokenStore === tokenStore
            ) {
              this.setState({...this.state, flagsReady: true});
            }
          });
        }
      }
    }

    if (!this.isPreview) {
      this.scheduleRefresh(tokenStore.expiresAt);
    }

    return tokenStore;
  }

  private onVerificationRequired(organization?: OrganizationInfo): void {
    this.clearRefresh();
    if (organization) {
      this.organization = organization;
    }
    this.setState({
      status: 'verification_required',
      organization: organization ?? this.organization,
    });
  }

  private onNotFound(): void {
    this.clearRefresh();
    this.setState({status: 'not_found'});
  }

  // The bootstrap and the scheduled refresh end in the same set of outcomes;
  // they differ only in what a transient failure means for the session.
  private async settle(
    request: Promise<TokenResponse>,
    isCurrent: () => boolean,
  ): Promise<Settled> {
    let response: TokenResponse;
    try {
      response = await request;
    } catch (err) {
      if (!isCurrent()) {
        return 'superseded';
      }
      if (err instanceof NotFoundError) {
        this.onNotFound();
        return 'terminal';
      }
      if (err instanceof AccessDeniedError) {
        // The stored verified identity is not invited to this page. Email
        // verification lets a different invited identity replace it.
        this.onVerificationRequired(err.organization);
        return 'terminal';
      }
      return err instanceof RateLimitedError ? 'rate_limited' : 'transient';
    }

    if (!isCurrent()) {
      return 'superseded';
    }
    if (isTokenSuccess(response)) {
      this.authenticate(
        response.data.token,
        response.data.organization_identifier,
        {isAnonymous: isAnonymousSession(response)},
      );
      return 'authenticated';
    }
    this.onVerificationRequired(response.organization);
    return 'terminal';
  }

  // A bootstrap superseded by a re-bootstrap or a dispose must not apply its
  // result, so each run only acts while it is still the active one.
  private bootstrap(): Promise<boolean> {
    const run = Symbol('bootstrap');
    const done = this.runBootstrap(() => this.activeBootstrap?.run === run);
    this.activeBootstrap = {run, done};
    return done;
  }

  private async runBootstrap(isCurrent: () => boolean): Promise<boolean> {
    const settled = await this.settle(
      requestTokenWithRetry(this.identifier),
      isCurrent,
    );
    if (settled === 'superseded') {
      return false;
    }

    // Only a definitive answer replaces a live session. A re-check that fails
    // for transient reasons leaves it in place, and the refresh timer keeps
    // retrying as it always did.
    if (this.state.status !== 'authenticated') {
      if (settled === 'rate_limited') {
        this.setState({status: 'rate_limited'});
      } else if (settled === 'transient') {
        this.setState({status: 'error'});
      }
    }

    this.activeBootstrap = null;
    return settled === 'authenticated';
  }

  private startPreview(jwt: string): void {
    sessionStorage.setItem(this.previewStorageKey, jwt);

    const tokenStore = this.authenticate(jwt, null);
    const expiresAtMs = tokenStore.expiresAt.getTime();

    if (isNaN(expiresAtMs) || expiresAtMs <= Date.now()) {
      this.expirePreview();
      return;
    }

    this.previewTimer = setTimeout(
      () => this.expirePreview(),
      expiresAtMs - Date.now(),
    );
  }

  private expirePreview(): void {
    sessionStorage.removeItem(this.previewStorageKey);
    this.setState({status: 'preview_expired'});
  }

  private clearRefresh(): void {
    this.refreshEpoch += 1;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private scheduleRefresh(expiresAt: Date): void {
    this.clearRefresh();
    const epoch = this.refreshEpoch;

    // Floored so a token already inside the buffer cannot refresh in a loop.
    const delayMs = Math.max(
      expiresAt.getTime() - Date.now() - REFRESH_BUFFER_MS,
      RETRY_DELAY_MS,
    );

    this.refreshTimer = setTimeout(() => void this.refresh(epoch), delayMs);
  }

  private async refresh(epoch: number): Promise<void> {
    const settled = await this.settle(
      requestToken(this.identifier),
      () => epoch === this.refreshEpoch,
    );
    if (settled === 'transient' || settled === 'rate_limited') {
      this.refreshTimer = setTimeout(
        () => void this.refresh(epoch),
        RETRY_DELAY_MS,
      );
    }
  }
}
